import chatApiClient from "../../helpers/services/chat-api-client";
import {
  SupportChatAppearance,
  SupportConversation,
  SupportMessage,
  SupportOnlineAgent,
  SupportVisitor,
  WidgetSessionResponse,
} from "../interfaces/types";

interface ConfigResponse {
  payload: { config: SupportChatAppearance };
}

interface ConversationsResponse {
  payload: { conversations: SupportConversation[] };
}

interface ConversationResponse {
  payload: { conversation: SupportConversation };
}

interface MessagesResponse {
  payload: { messages: SupportMessage[] };
}

interface VisitorsResponse {
  payload: { visitors: SupportVisitor[] };
}

interface OnlineAgentsResponse {
  payload: { agents: SupportOnlineAgent[] };
}

interface SessionResponse {
  payload: WidgetSessionResponse;
}

interface ActiveConversationResponse {
  payload: { conversation: SupportConversation | null };
}

export const wembiiSupportApiService = {
  getConfig() {
    return chatApiClient()
      .get<ConfigResponse>("wembii-support/config")
      .then(({ data }) => data.payload.config);
  },

  updateConfig(config: Partial<SupportChatAppearance>) {
    return chatApiClient()
      .put<ConfigResponse>("wembii-support/config", config)
      .then(({ data }) => data.payload.config);
  },

  postAvatar(dataUri: Blob) {
    const body = new FormData();
    body.append("images", dataUri);
    return chatApiClient()
      .post<ConfigResponse>("wembii-support/config/avatar", body)
      .then(({ data }) => data.payload.config);
  },

  getConversations(params?: { status?: string; search?: string }) {
    return chatApiClient()
      .get<ConversationsResponse>("wembii-support/conversations", { params })
      .then(({ data }) => data.payload.conversations);
  },

  getConversation(id: string) {
    return chatApiClient()
      .get<ConversationResponse>(`wembii-support/conversations/${id}`)
      .then(({ data }) => data.payload.conversation);
  },

  getMessages(conversationId: string) {
    return chatApiClient()
      .get<MessagesResponse>(`wembii-support/conversations/${conversationId}/messages`)
      .then(({ data }) => data.payload.messages);
  },

  updateStatus(conversationId: string, status: string) {
    return chatApiClient()
      .patch<ConversationResponse>(`wembii-support/conversations/${conversationId}/status`, {
        status,
      })
      .then(({ data }) => data.payload.conversation);
  },

  createSession(companyId: string, metadata: Record<string, unknown>) {
    return chatApiClient()
      .post<SessionResponse>("wembii-support/session", { companyId, metadata })
      .then(({ data }) => data.payload);
  },

  endSession(conversationId: string) {
    return chatApiClient()
      .post("wembii-support/session/offline", { conversationId })
      .then(() => undefined);
  },

  getActiveConversation() {
    return chatApiClient()
      .get<ActiveConversationResponse>("wembii-support/conversations/active/me")
      .then(({ data }) => data.payload.conversation);
  },

  getVisitors() {
    return chatApiClient()
      .get<VisitorsResponse>("wembii-support/visitors")
      .then(({ data }) => data.payload.visitors);
  },

  getOnlineAgents() {
    return chatApiClient()
      .get<OnlineAgentsResponse>("wembii-support/agents/online")
      .then(({ data }) => data.payload.agents);
  },
};
